'use client';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import {
  FORM_COMPONENT_BASE_STYLES,
  type FormComponentSize,
  getFormComponentSizeClasses,
} from '@ui/lib/form-sizing';
import { cn } from '@ui/lib/utils';
import { ChevronDown, SearchIcon } from 'lucide-react';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

// Re-export the standardized size type for consistency
export type ComboboxSize = FormComponentSize;

interface ComboboxBaseProps<T> {
  getDisplayValue: (item: T) => string;
  getId: (item: T) => string;
  renderOption: (props: {
    item: T;
    active: boolean;
    selected: boolean;
  }) => ReactElement;
  renderSelectedItem: (props: {
    item: T;
    onRemove: () => void;
    disabled?: boolean;
  }) => ReactElement;
  placeholder?: string;
  disabled?: boolean;
  size?: ComboboxSize;
  containerClassName?: string;
  borderClassName?: string;
}

interface ServerComboboxProps<T> extends ComboboxBaseProps<T> {
  initialSelectedItem?: T | null;
  onSelect: (item: T | null) => void;
  fetchOptions: (query: string) => Promise<T[]>;
  queryKey: string;
  loadingComponent?: ReactElement;
}

export function ServerCombobox<T>({
  initialSelectedItem = null,
  onSelect,
  fetchOptions,
  queryKey,
  getDisplayValue,
  getId,
  renderOption,
  renderSelectedItem,
  placeholder = 'Start Typing...',
  disabled = false,
  size = 'md',
  loadingComponent,
  borderClassName,
}: ServerComboboxProps<T>) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [selectedItem, setSelectedItem] = useState<T | null>(
    initialSelectedItem
  );

  const sizeClasses = getFormComponentSizeClasses(size);

  // Update selectedItem when initialSelectedItem changes
  useEffect(() => {
    if (initialSelectedItem !== null) {
      setSelectedItem(initialSelectedItem);
    }
  }, [initialSelectedItem]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(query), 500);
    return () => clearTimeout(handler);
  }, [query]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey, debouncedQuery],
    queryFn: () => fetchOptions(debouncedQuery),
    enabled: !!debouncedQuery && selectedItem === null,
  });

  const selectItem = useCallback(
    (item: T) => {
      setSelectedItem(item);
      onSelect(item);
      setQuery('');
    },
    [onSelect]
  );

  const removeItem = useCallback(() => {
    setSelectedItem(null);
    onSelect(null);
  }, [onSelect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && query === '' && selectedItem) {
        removeItem();
      }
    },
    [query, selectedItem, removeItem]
  );

  return (
    <Combobox
      value={selectedItem}
      onChange={selectItem}
      disabled={disabled}
      by={(a: T | null, b: T | null) => {
        // Handle null values in comparison
        if (a === null || b === null) return a === b;
        // Use the getId function for comparison to ensure we respect composite IDs
        return getId(a) === getId(b);
      }}
    >
      <div className={cn('relative', FORM_COMPONENT_BASE_STYLES.margin)}>
        <div
          className={cn(
            'flex items-center',
            FORM_COMPONENT_BASE_STYLES.border,
            FORM_COMPONENT_BASE_STYLES.focus,
            disabled && FORM_COMPONENT_BASE_STYLES.disabledContainer,
            sizeClasses.height,
            sizeClasses.padding,
            borderClassName
          )}
        >
          <ComboboxButton
            className="flex-shrink-0 flex items-center gap-0.5"
            disabled={disabled}
          >
            <SearchIcon
              className={cn(
                'text-slate-800 mt-[1.5px]',
                disabled && 'text-slate-300',
                sizeClasses.searchIcon,
                sizeClasses.searchIconMargin
              )}
              aria-hidden="true"
            />
            <ChevronDown
              className={cn(
                'text-slate-700',
                disabled && 'text-slate-300',
                sizeClasses.chevron,
                sizeClasses.chevronMargin
              )}
              aria-hidden="true"
            />
          </ComboboxButton>
          {selectedItem ? (
            <div
              className={cn(
                'flex flex-1 items-center min-w-0 pr-2',
                sizeClasses.selectedItemPadding
              )}
            >
              {renderSelectedItem({
                item: selectedItem,
                onRemove: removeItem,
                disabled,
              })}
            </div>
          ) : (
            <ComboboxInput
              className={cn(
                'w-full bg-transparent focus:outline-none',
                FORM_COMPONENT_BASE_STYLES.text,
                FORM_COMPONENT_BASE_STYLES.placeholder,
                disabled && 'cursor-not-allowed',
                sizeClasses.inputMulti
              )}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              displayValue={getDisplayValue}
              value={query}
              placeholder={placeholder}
              disabled={disabled}
            />
          )}
        </div>

        {isLoading && (
          <div
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {loadingComponent || (
              <div className="animate-pulse flex items-center px-3 py-1 gap-2">
                <div className="bg-slate-200 rounded-full h-6 w-6"></div>
                <div className="bg-slate-200 rounded-lg h-4 w-32"></div>
              </div>
            )}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <ComboboxOptions
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {items.map((item: T) => (
              <ComboboxOption
                key={getId(item)}
                value={item}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none transition-transform duration-75',
                    active
                      ? 'bg-slate-200 text-slate-800'
                      : 'bg-white text-slate-800',
                    'active:scale-[0.985]',
                    sizeClasses.option
                  )
                }
              >
                {({ active, selected }) =>
                  renderOption({ item, active, selected })
                }
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}

interface ClientComboboxProps<T> extends ComboboxBaseProps<T> {
  items: T[];
  selectedItem: T | null;
  onSelect: (item: T | null) => void;
  borderClassName?: string;
}

export function ClientCombobox<T>({
  items,
  selectedItem,
  onSelect,
  getDisplayValue,
  getId,
  renderOption,
  renderSelectedItem,
  placeholder = 'Start Typing...',
  disabled = false,
  size = 'md',
  borderClassName,
}: ClientComboboxProps<T>) {
  const [query, setQuery] = useState('');
  const sizeClasses = getFormComponentSizeClasses(size);

  const filteredItems = useMemo(
    () =>
      query === ''
        ? items.slice(0, 10)
        : items
            .filter((item) =>
              getDisplayValue(item).toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 10),
    [items, query, getDisplayValue]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && query === '' && selectedItem) {
        onSelect(null);
      }
    },
    [query, selectedItem, onSelect]
  );

  return (
    <Combobox
      value={selectedItem}
      onChange={onSelect}
      disabled={disabled}
      by={(a: T | null, b: T | null) => {
        // Handle null values in comparison
        if (a === null || b === null) return a === b;
        // Use the getId function for comparison to ensure we respect composite IDs
        return getId(a) === getId(b);
      }}
    >
      <div className={cn('relative', FORM_COMPONENT_BASE_STYLES.margin)}>
        <div
          className={cn(
            'flex items-center',
            FORM_COMPONENT_BASE_STYLES.border,
            FORM_COMPONENT_BASE_STYLES.focus,
            disabled && FORM_COMPONENT_BASE_STYLES.disabledContainer,
            sizeClasses.height,
            sizeClasses.padding,
            borderClassName
          )}
        >
          <ComboboxButton
            className="flex-shrink-0 flex items-center gap-0.5"
            disabled={disabled}
          >
            <SearchIcon
              className={cn(
                'text-slate-800 mt-[1.5px]',
                disabled && 'text-slate-300',
                sizeClasses.searchIcon,
                sizeClasses.searchIconMargin
              )}
              aria-hidden="true"
            />
            <ChevronDown
              className={cn(
                'text-slate-700',
                disabled && 'text-slate-300',
                sizeClasses.chevron,
                sizeClasses.chevronMargin
              )}
              aria-hidden="true"
            />
          </ComboboxButton>
          {selectedItem ? (
            <div
              className={cn(
                'flex flex-1 items-center min-w-0 pr-2',
                sizeClasses.selectedItemPadding
              )}
            >
              {renderSelectedItem({
                item: selectedItem,
                onRemove: () => onSelect(null),
                disabled,
              })}
            </div>
          ) : (
            <ComboboxInput
              className={cn(
                'w-full bg-transparent focus:outline-none',
                FORM_COMPONENT_BASE_STYLES.text,
                FORM_COMPONENT_BASE_STYLES.placeholder,
                disabled && 'cursor-not-allowed',
                sizeClasses.inputMulti
              )}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              displayValue={getDisplayValue}
              placeholder={placeholder}
              disabled={disabled}
            />
          )}
        </div>

        {filteredItems.length > 0 && (
          <ComboboxOptions
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {filteredItems.map((item: T) => (
              <ComboboxOption
                key={getId(item)}
                value={item}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none transition-transform duration-75',
                    active
                      ? 'bg-slate-200 text-slate-800'
                      : 'bg-white text-slate-800',
                    'active:scale-[0.985]',
                    sizeClasses.option
                  )
                }
              >
                {({ active, selected }) =>
                  renderOption({ item, active, selected })
                }
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}

interface MultiSelectServerComboboxProps<T> extends ComboboxBaseProps<T> {
  initialSelectedItems?: T[];
  onSelect: (items: T[]) => void;
  fetchOptions: (query: string) => Promise<T[]>;
  queryKey: string;
  loadingComponent?: ReactElement;
}

export function MultiSelectServerCombobox<T>({
  initialSelectedItems = [],
  onSelect,
  fetchOptions,
  queryKey,
  getDisplayValue,
  getId,
  renderOption,
  renderSelectedItem,
  placeholder = 'Start Typing...',
  disabled = false,
  size = 'md',
  loadingComponent,
  containerClassName,
}: MultiSelectServerComboboxProps<T>) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [selectedItems, setSelectedItems] = useState<T[]>(initialSelectedItems);

  const sizeClasses = getFormComponentSizeClasses(size);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(query), 500);
    return () => clearTimeout(handler);
  }, [query]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey, debouncedQuery],
    queryFn: () => fetchOptions(debouncedQuery),
    enabled: !!debouncedQuery,
  });

  const filteredItems = useMemo(() => {
    return items.filter(
      (item: T) =>
        !selectedItems.some((selected) => getId(selected) === getId(item))
    );
  }, [items, selectedItems, getId]);

  const selectItem = useCallback(
    (item: T) => {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);
      onSelect(newSelectedItems);
      setQuery('');
    },
    [selectedItems, onSelect]
  );

  const removeItem = useCallback(
    (itemToRemove: T) => {
      const newSelectedItems = selectedItems.filter(
        (item) => getId(item) !== getId(itemToRemove)
      );
      setSelectedItems(newSelectedItems);
      onSelect(newSelectedItems);
    },
    [selectedItems, onSelect, getId]
  );

  return (
    <Combobox<T | null>
      value={null}
      onChange={(value: T | null) => value && selectItem(value)}
      disabled={disabled}
      by={(a: T | null, b: T | null) => {
        // Handle null values in comparison
        if (a === null || b === null) return a === b;
        // Use the getId function for comparison to ensure we respect composite IDs
        return getId(a) === getId(b);
      }}
    >
      <div className={cn('relative', FORM_COMPONENT_BASE_STYLES.margin)}>
        <div
          className={cn(
            'flex flex-wrap items-start',
            FORM_COMPONENT_BASE_STYLES.border,
            FORM_COMPONENT_BASE_STYLES.focus,
            disabled && FORM_COMPONENT_BASE_STYLES.disabledContainer,
            sizeClasses.containerMulti,
            sizeClasses.paddingMulti,
            containerClassName
          )}
        >
          <div className="flex items-center self-start mr-1 min-h-[30px]">
            <ComboboxButton
              className="flex-shrink-0 flex items-center gap-0.5"
              disabled={disabled}
            >
              <SearchIcon
                className={cn(
                  'text-slate-800 mt-[1.5px]',
                  disabled && 'text-slate-300',
                  sizeClasses.searchIcon,
                  sizeClasses.searchIconMargin
                )}
                aria-hidden="true"
              />
              <ChevronDown
                className={cn(
                  'text-slate-700',
                  disabled && 'text-slate-300',
                  !selectedItems.length && 'animate-bounce',
                  sizeClasses.chevron,
                  sizeClasses.chevronMargin
                )}
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <div className="flex flex-wrap items-start gap-1 flex-1">
            {selectedItems.map((item: T) => (
              <div key={getId(item)} className="flex-shrink-0">
                {renderSelectedItem({
                  item,
                  onRemove: () => removeItem(item),
                  disabled,
                })}
              </div>
            ))}
            <div className="flex-1 min-w-[100px]">
              <ComboboxInput
                className={cn(
                  'w-full bg-transparent focus:outline-none',
                  FORM_COMPONENT_BASE_STYLES.text,
                  FORM_COMPONENT_BASE_STYLES.placeholder,
                  disabled && 'cursor-not-allowed',
                  sizeClasses.inputMulti
                )}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    query === '' &&
                    selectedItems.length > 0
                  ) {
                    const lastItem = selectedItems[
                      selectedItems.length - 1
                    ] as T;
                    removeItem(lastItem);
                  }
                }}
                displayValue={() => query}
                placeholder={selectedItems.length === 0 ? placeholder : ''}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {isLoading && (
          <div
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {loadingComponent || (
              <div className="animate-pulse flex items-center px-3 py-1 gap-2">
                <div className="bg-slate-200 rounded-full h-6 w-6"></div>
                <div className="bg-slate-200 rounded-lg h-4 w-32"></div>
              </div>
            )}
          </div>
        )}

        {!isLoading && filteredItems.length > 0 && (
          <ComboboxOptions
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {filteredItems.map((item: T) => (
              <ComboboxOption
                key={getId(item)}
                value={item}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none transition-transform duration-75',
                    active
                      ? 'bg-slate-200 text-slate-800'
                      : 'bg-white text-slate-800',
                    'active:scale-[0.985]',
                    sizeClasses.option
                  )
                }
              >
                {({ active, selected }) =>
                  renderOption({ item, active, selected })
                }
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}

interface MultiSelectClientComboboxProps<T> extends ComboboxBaseProps<T> {
  items: T[];
  selectedItems: T[];
  onSelect: (items: T[]) => void;
}

export function MultiSelectClientCombobox<T>({
  items,
  selectedItems,
  onSelect,
  getDisplayValue,
  getId,
  renderOption,
  renderSelectedItem,
  placeholder = 'Start Typing...',
  disabled = false,
  size = 'md',
  containerClassName,
}: MultiSelectClientComboboxProps<T>) {
  const [query, setQuery] = useState('');
  const sizeClasses = getFormComponentSizeClasses(size);

  const filteredItems = useMemo(() => {
    const unselectedItems = items.filter(
      (item) =>
        !selectedItems.some((selected) => getId(selected) === getId(item))
    );

    return query === ''
      ? unselectedItems.slice(0, 10)
      : unselectedItems
          .filter((item) =>
            getDisplayValue(item).toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 10);
  }, [items, selectedItems, query, getDisplayValue, getId]);

  const selectItem = useCallback(
    (item: T) => {
      if (!selectedItems.some((selected) => getId(selected) === getId(item))) {
        const newSelectedItems = [...selectedItems, item];
        onSelect(newSelectedItems);
      }
      setQuery('');
    },
    [selectedItems, onSelect, getId]
  );

  const removeItem = useCallback(
    (itemToRemove: T) => {
      const newSelectedItems = selectedItems.filter(
        (item) => getId(item) !== getId(itemToRemove)
      );
      onSelect(newSelectedItems);
    },
    [selectedItems, onSelect, getId]
  );

  return (
    <Combobox<T | null>
      value={null}
      onChange={(value: T | null) => value && selectItem(value)}
      disabled={disabled}
      by={(a: T | null, b: T | null) => {
        // Handle null values in comparison
        if (a === null || b === null) return a === b;
        // Use the getId function for comparison to ensure we respect composite IDs
        return getId(a) === getId(b);
      }}
    >
      <div className={cn('relative', FORM_COMPONENT_BASE_STYLES.margin)}>
        <div
          className={cn(
            'flex flex-wrap items-start',
            FORM_COMPONENT_BASE_STYLES.border,
            FORM_COMPONENT_BASE_STYLES.focus,
            disabled && FORM_COMPONENT_BASE_STYLES.disabledContainer,
            sizeClasses.containerMulti,
            sizeClasses.paddingMulti,
            containerClassName
          )}
        >
          <div className="flex items-center self-start mr-1 min-h-[30px]">
            <ComboboxButton
              className="flex-shrink-0 flex items-center gap-0.5"
              disabled={disabled}
            >
              <SearchIcon
                className={cn(
                  'text-slate-800 mt-[1.5px]',
                  disabled && 'text-slate-300',
                  sizeClasses.searchIcon,
                  sizeClasses.searchIconMargin
                )}
                aria-hidden="true"
              />
              <ChevronDown
                className={cn(
                  'text-slate-700',
                  disabled && 'text-slate-300',
                  !selectedItems.length && 'animate-bounce',
                  sizeClasses.chevron,
                  sizeClasses.chevronMargin
                )}
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <div className="flex flex-wrap items-start gap-1 flex-1">
            {selectedItems.map((item: T) => (
              <div key={getId(item)} className="flex-shrink-0">
                {renderSelectedItem({
                  item,
                  onRemove: () => removeItem(item),
                  disabled,
                })}
              </div>
            ))}
            <div className="flex-1 min-w-[100px]">
              <ComboboxInput
                className={cn(
                  'w-full bg-transparent focus:outline-none',
                  FORM_COMPONENT_BASE_STYLES.text,
                  FORM_COMPONENT_BASE_STYLES.placeholder,
                  disabled && 'cursor-not-allowed',
                  sizeClasses.inputMulti
                )}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    query === '' &&
                    selectedItems.length > 0
                  ) {
                    const lastItem = selectedItems[
                      selectedItems.length - 1
                    ] as T;
                    removeItem(lastItem);
                  }
                }}
                displayValue={() => query}
                placeholder={selectedItems.length === 0 ? placeholder : ''}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {filteredItems.length > 0 && (
          <ComboboxOptions
            className={cn(
              'absolute z-10 top-[calc(100%_+_8px)] w-full max-h-60 overflow-auto rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              sizeClasses.text
            )}
          >
            {filteredItems.map((item: T) => (
              <ComboboxOption
                key={getId(item)}
                value={item}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none transition-transform duration-75',
                    active
                      ? 'bg-slate-200 text-slate-800'
                      : 'bg-white text-slate-800',
                    'active:scale-[0.985]',
                    sizeClasses.option
                  )
                }
              >
                {({ active, selected }) =>
                  renderOption({ item, active, selected })
                }
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
